import { faSoapNote } from "@awesome.me/kit-0572d5a008/icons/kit/custom";
import RecordingJobTypeSelection from "@components/common/record/RecordingJobTypeSelection";
import {
  faAlignLeft,
  faFile,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useTemplates,
  useLocalStorage,
  STRING_SERIALIZATION,
} from "@hooks";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const ClientSummaryOptionButton = ({
  onClick,
  dataTestId,
  icon,
  title,
  description,
}) => {
  return (
    <button
      className={`group p-4 rounded-xl border dark:border-gray-600 flex-1 hover:bg-indigo-500/20 hover:border-indigo-300 dark:hover:border-indigo-500 transition-all shadow-xs`}
      onClick={onClick}
      data-testid={dataTestId}
    >
      <div className="flex flex-row items-center space-x-4">
        <FontAwesomeIcon
          icon={icon}
          size="2x"
          className="text-gray-400 dark:text-gray-500 group-hover:text-indigo-500 transition-all"
        />

        <div className="flex flex-col items-start text-left">
          <h4>{title}</h4>
          <h5 className="text-sm">{description}</h5>
        </div>
      </div>
    </button>
  );
};

const SelectType = ({
  handleGenerateDefaultClientSummary,
  handleGenerateAdaptiveClientSummary,
}) => {
  const { allTemplates, findTemplateByUuid } = useTemplates();
  const navigate = useNavigate();

  const defaultTemplate = allTemplates[0];

  const [storedTemplateUuid, setStoredTemplateUuid] = useLocalStorage(
    "clientSummaryTemplateUuid",
    null,
    STRING_SERIALIZATION,
  );

  const [selectedJobType, setSelectedJobType] = useState(null);

  const mapTemplateToJobType = (template) => ({
    value: template.uuid,
    name: template.name,
    icon: template.templateType === "SOAP" ? faSoapNote : faFile,
    description: template.description,
  });

  const setSelectedJobTypeAndPersist = (template) => {
    setSelectedJobType(template);
    const templateUuid = template?.value;
    if (templateUuid) {
      setStoredTemplateUuid(templateUuid);
    }
  };

  const currentSelectedTemplate = selectedJobType?.value
    ? findTemplateByUuid(selectedJobType.value)
    : null;

  const fallbackTemplate = storedTemplateUuid
    ? findTemplateByUuid(storedTemplateUuid) || defaultTemplate
    : defaultTemplate;

  const effectiveSelectedJobType = currentSelectedTemplate
    ? selectedJobType
    : fallbackTemplate
      ? mapTemplateToJobType(fallbackTemplate)
      : null;

  return (
    <div className="p-2">
      {allTemplates.length > 0 && (
        <div className="mb-4">
          <RecordingJobTypeSelection
            selectedJobType={effectiveSelectedJobType}
            setSelectedJobType={setSelectedJobTypeAndPersist}
            hideScribenoteDefaults={true}
            defaultJobType={defaultTemplate}
          />
        </div>
      )}
      <div className="flex justify-center space-x-4">
        {allTemplates.length > 0 ? (
          <ClientSummaryOptionButton
            onClick={() =>
              handleGenerateAdaptiveClientSummary(
                effectiveSelectedJobType,
              )
            }
            dataTestId="adaptive-client-summary-button"
            icon={faFile}
            title="Adaptive Client Summary"
            description={
              <>
                Use{" "}
                <span className="font-bold">
                  {effectiveSelectedJobType?.name ||
                    defaultTemplate?.name}
                </span>{" "}
                to create a client summary.
              </>
            }
          />
        ) : (
          <ClientSummaryOptionButton
            title="Adaptive Client Summary"
            description="No adaptive templates found. Please create a template to use this feature."
            icon={faFile}
            onClick={() => navigate("/templates")}
            dataTestId="adaptive-client-empty-button"
          />
        )}
        <ClientSummaryOptionButton
          onClick={handleGenerateDefaultClientSummary}
          dataTestId="simple-client-summary-button"
          icon={faAlignLeft}
          title="Default Client Summary"
          description="Automatically generates a client summary based on the information in the note. Includes Findings, Treatments, and Recommendations for the client."
        />
      </div>
    </div>
  );
};

export default SelectType;
